import React from 'react';
import { FIG_INK, FIG_TEXT, FIG_RED } from '../figureStyle';

// ─── CVE spike after the Mythos preview (tiny at-a-glance sketch) ────────────
// For the epoch.ai/gradient-updates "are Mythos cyber capabilities overhyped"
// link tooltip: minimalist redraw of Epoch's "Critical and high severity
// vulnerabilities from 21 notable organizations" chart. A noisy red band of
// high-severity CVEs oscillates 100–370/month from 2022 on, then shoots to
// ~900 in the months after the Claude Mythos Preview announcement (dashed
// vertical); critical CVEs (ink) hug the floor and tick up alongside. Same
// visual language as the other simple-* sketches: dashed gridlines, no axis
// lines, red as the one strong accent — plus a title, unit subtitle, and
// legend since this one summarizes a real external chart.

const YEAR_MIN = 2022;
const YEAR_MAX = 2026.62;
const ANNOUNCED = 2026.29; // Claude Mythos Preview announcement
const CVE_MAX = 950; // CVEs/month at the top of the plot

// [year, CVEs/month] — eyeballed monthly-ish samples of Epoch's series, kept
// jagged so the pre-announcement stretch reads as ordinary reporting noise.
const HIGH: [number, number][] = [
  [2022.0, 150], [2022.17, 85], [2022.33, 160], [2022.5, 100], [2022.67, 225], [2022.83, 145],
  [2023.0, 265], [2023.17, 195], [2023.33, 230], [2023.5, 165], [2023.7, 145], [2023.83, 205],
  [2024.0, 135], [2024.17, 225], [2024.33, 255], [2024.5, 185], [2024.67, 275], [2024.83, 210],
  [2025.0, 305], [2025.17, 245], [2025.3, 310], [2025.45, 230], [2025.55, 150], [2025.7, 260], [2025.8, 370],
  [2025.92, 225], [2026.0, 205], [2026.08, 215], [2026.17, 260], [2026.25, 380], [2026.33, 600], [2026.42, 900],
];
const CRITICAL: [number, number][] = [
  [2022.0, 20], [2022.25, 5], [2022.5, 15], [2022.75, 10],
  [2023.0, 30], [2023.25, 25], [2023.5, 12], [2023.75, 15],
  [2024.0, 15], [2024.25, 30], [2024.5, 25], [2024.75, 20],
  [2025.0, 35], [2025.2, 55], [2025.4, 25], [2025.6, 60], [2025.8, 30],
  [2026.0, 25], [2026.17, 45], [2026.25, 55], [2026.33, 95], [2026.42, 140],
];

const VX = -18;
const VW = 312;
const VH = 144;
const PLOT_L = 8;
const PLOT_R = 292;
const PLOT_T = 34;
const PLOT_B = 126;

const LABEL_FS = 8;

const x = (year: number) => PLOT_L + ((year - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * (PLOT_R - PLOT_L);
const y = (cves: number) => PLOT_B - (cves / CVE_MAX) * (PLOT_B - PLOT_T);
const path = (pts: [number, number][]) =>
  pts.map(([yr, v], i) => `${i === 0 ? 'M' : 'L'}${x(yr).toFixed(2)},${y(v).toFixed(2)}`).join(' ');

const HIGH_END = HIGH[HIGH.length - 1];

export default function SimpleCveSpike() {
  return (
    <div className="mx-auto mt-4">
      <svg viewBox={`${VX} 0 ${VW} ${VH}`} className="w-full h-auto font-mono">
        {/* Title + unit subtitle, aligned with the y-label column */}
        <text x={-10} y={11} fontSize={10} fontWeight={700} fill={FIG_TEXT}>
          Critical &amp; high-severity CVEs per month
        </text>
        <text x={-10} y={23} fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.65}>
          reported by 21 notable organizations
        </text>

        {/* Legend, right-aligned on the subtitle line */}
        <line x1={198} y1={20.2} x2={208} y2={20.2} stroke={FIG_RED} strokeWidth={2.5} />
        <text x={231.6} y={23} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT}>
          High
        </text>
        <line x1={239.6} y1={20.2} x2={249.6} y2={20.2} stroke={FIG_INK} strokeWidth={2.5} />
        <text x={292} y={23} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT}>
          Critical
        </text>

        {[0, 300, 600, 900].map((cves) => (
          <g key={cves}>
            <line
              x1={PLOT_L} y1={y(cves)} x2={PLOT_R} y2={y(cves)}
              stroke={FIG_INK} strokeWidth={0.75} strokeDasharray="2 3"
              opacity={cves === 0 ? 0.55 : 0.32}
            />
            <text x={PLOT_L - 4} y={y(cves) + 2} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT}>
              {cves}
            </text>
          </g>
        ))}

        {[2022, 2023, 2024, 2025, 2026].map((yr) => (
          <text key={yr} x={x(yr)} y={PLOT_B + 12} textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT}>
            {yr}
          </text>
        ))}

        {/* Mythos Preview announcement marker */}
        <line
          x1={x(ANNOUNCED)} y1={PLOT_T} x2={x(ANNOUNCED)} y2={PLOT_B}
          stroke={FIG_INK} strokeWidth={0.9} strokeDasharray="3 3" opacity={0.55}
        />
        <text x={x(ANNOUNCED) - 5} y={55} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.8}>
          Mythos Preview
        </text>
        <text x={x(ANNOUNCED) - 5} y={65} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.8}>
          announced
        </text>

        <path
          d={path(CRITICAL)} fill="none" stroke={FIG_INK}
          strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
        />
        <path
          d={path(HIGH)} fill="none" stroke={FIG_RED}
          strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
        />
        <circle cx={x(HIGH_END[0])} cy={y(HIGH_END[1])} r={2.5} fill={FIG_RED} />
      </svg>
    </div>
  );
}
